import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import { collection, getDocs } from 'firebase/firestore';
import { DateRange } from 'react-date-range';
import { format, addDays } from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import './index.css';
import { toast } from 'react-hot-toast';

function VillaBooking({ villa: propVilla, dateRange: propDateRange, adults: propAdults, children: propChildren }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [villa, setVilla] = useState(propVilla || null);
  const [dates, setDates] = useState(
    propDateRange
      ? {
          startDate: new Date(propDateRange.startDate),
          endDate: new Date(propDateRange.endDate),
          key: 'selection',
        }
      : {
          startDate: new Date(),
          endDate: addDays(new Date(), 2),
          key: 'selection',
        }
  );
  const [adults, setAdults] = useState(propAdults || 1);
  const [children, setChildren] = useState(propChildren || 0);
  const [nights, setNights] = useState(2);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(!propVilla);
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef(null);

  useEffect(() => {
    async function fetchVilla() {
      if (!villa && slug) {
        try {
          setLoading(true);
          const villasRef = collection(db, 'villas');
          const snapshot = await getDocs(villasRef);
          const villas = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          const matchingVilla = villas.find(v => v.title.toLowerCase().replace(/\s+/g, '-') === slug);
          if (matchingVilla) {
            setVilla(matchingVilla);
          } else {
            setError('Villa not found');
            toast.error('Villa not found', { duration: 4000 });
          }
        } catch (err) {
          console.error('Error fetching villa:', err);
          setError('Failed to load villa');
          toast.error('Failed to load villa', { duration: 4000 });
        } finally {
          setLoading(false);
        }
      }
    }
    fetchVilla();
  }, [slug, villa]);

  const isDateBlocked = useCallback(
    date => villa?.blockedDates?.some(blocked => format(new Date(blocked), 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')) || false,
    [villa]
  );

  useEffect(() => {
    const isRangeValid = (start, end) => {
      if (!villa?.blockedDates) return true;
      const range = [];
      let current = new Date(start);
      while (current <= end) {
        range.push(new Date(current));
        current = addDays(current, 1);
      }
      return !range.some(isDateBlocked);
    };

    const { startDate: checkIn, endDate: checkOut } = dates;
    if (checkIn && checkOut && villa && checkIn !== checkOut) {
      const timeDiff = checkOut - checkIn;
      const calculatedNights = Math.max(0, Math.ceil(timeDiff / (1000 * 60 * 60 * 24)));
      if (calculatedNights < 2) {
        toast.error('Minimum 2 nights required', { duration: 4000 });
        setNights(0);
        setTotal(0);
        return;
      }
      if (!isRangeValid(checkIn, checkOut)) {
        toast.error('Selected dates are unavailable', { duration: 4000 });
        setNights(0);
        setTotal(0);
        return;
      }
      setNights(calculatedNights);
      const basePrice = parseFloat(villa.basePricePerNight) || 0;
      const deposit = parseFloat(villa.securityDeposit) || 0;
      const vatRate = parseFloat(villa.vatGst) / 100 || 0.18;
      const subtotal = calculatedNights * basePrice;
      const vat = subtotal * vatRate;
      setTotal(subtotal + vat + deposit);
    } else {
      setNights(0);
      setTotal(0);
    }
  }, [dates, villa, isDateBlocked]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAdultChange = increment => {
    const newAdults = adults + increment;
    const max = parseInt(villa?.maxGuests) || 12;
    if (newAdults >= 1 && newAdults + children <= max) {
      setAdults(newAdults);
    } else if (newAdults < 1) {
      toast.error('At least one adult required', { duration: 4000 });
    } else {
      toast.error(`Maximum ${max} guests allowed`, { duration: 4000 });
    }
  };

  const handleChildrenChange = increment => {
    const newChildren = children + increment;
    const max = parseInt(villa?.maxGuests) || 12;
    if (newChildren >= 0 && adults + newChildren <= max) {
      setChildren(newChildren);
    } else if (newChildren < 0) {
      toast.error('Cannot have negative children', { duration: 4000 });
    } else {
      toast.error(`Maximum ${max} guests allowed`, { duration: 4000 });
    }
  };

  const handleDateSelect = ranges => {
    const { selection } = ranges;
    const { startDate, endDate } = selection;
    
    if (startDate && endDate && startDate !== endDate) {
      const timeDiff = endDate - startDate;
      const nights = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
      if (nights < 2) {
        toast.error('Minimum 2 nights required', { duration: 4000 });
        setDates({
          startDate: startDate,
          endDate: addDays(startDate, 2),
          key: 'selection',
        });
        return;
      }
    }

    setDates({
      startDate: startDate,
      endDate: endDate || startDate,
      key: 'selection',
    });

    if (endDate && startDate !== endDate && nights >= 2) {
      setShowCalendar(false);
    }
  };

  const handleReserve = () => {
    if (nights < 2) {
      toast.error('Please select at least 2 nights', { duration: 4000 });
      return;
    }
    navigate(`/villa/${slug}/booking`, {
      state: {
        villa,
        dates,
        adults,
        children,
        nights,
        total,
      },
    });
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!villa) return <div className="no-data">No villa available</div>;

  const formattedDate = dates.startDate && dates.endDate && dates.startDate !== dates.endDate
    ? `${format(dates.startDate, 'MMM dd, yyyy')} - ${format(dates.endDate, 'MMM dd, yyyy')}`
    : 'Select dates';

  return (
    <div className="villa-booking-container">
      <div className="booking-row">
        <div className="image-containerBooking">
          <img src={villa.imageUrls[1]} alt={villa.title} className="villa-imageBooking" />
        </div>
        <div className="booking-panel">
          <h1 className="villa-title">{villa.title}</h1>
          <h2 className="booking-subtitle">Book Stay</h2>
          <p className="night-cost">
            ₹{(parseFloat(villa.basePricePerNight) || 0).toFixed(2)} per night
          </p>
          <div className="form-group">
            <label className="form-label">Select Dates</label>
            <input
              type="text"
              className="date-input"
              value={formattedDate}
              onClick={() => setShowCalendar(!showCalendar)}
              readOnly
            />
            {showCalendar && (
              <div className="calendar-container" ref={calendarRef}>
                <DateRange
                  editableDateInputs
                  onChange={handleDateSelect}
                  moveRangeOnFirstSelection={false}
                  ranges={[dates]}
                  className="date-range"
                  locale={enUS}
                  disabledDates={villa.blockedDates?.map(date => new Date(date)) || []}
                  minDate={new Date()}
                  direction="horizontal"
                />
              </div>
            )}
          </div>
          <div className="guest-row">
            <div className="guest-group">
              <label className="form-label">Adults</label>
              <div className="number-input">
                <button onClick={() => handleAdultChange(-1)} className="number-btn">-</button>
                <span className="number-value">{adults}</span>
                <button onClick={() => handleAdultChange(1)} className="number-btn">+</button>
              </div>
            </div>
            <div className="guest-group">
              <label className="form-label">Children</label>
              <div className="number-input">
                <button onClick={() => handleChildrenChange(-1)} className="number-btn">-</button>
                <span className="number-value">{children}</span>
                <button onClick={() => handleChildrenChange(1)} className="number-btn">+</button>
              </div>
            </div>
          </div>
          <div className="price-breakdown">
            <div className="price-row">
              <span>{nights} Night{nights !== 1 ? 's' : ''}</span>
              <span>₹{(nights * (parseFloat(villa.basePricePerNight) || 0)).toFixed(2)}</span>
            </div>
            <div className="price-row">
              <span>VAT / GST ({(parseFloat(villa.vatGst) || 18).toFixed(0)}%)</span>
              <span>₹{((nights * (parseFloat(villa.basePricePerNight) || 0)) * (parseFloat(villa.vatGst) / 100 || 0.18)).toFixed(2)}</span>
            </div>
            <div className="price-row">
              <span>Refundable Security Deposit</span>
              <span>₹{(parseFloat(villa.securityDeposit) || 0).toFixed(2)}</span>
            </div>
            <div className="price-row total">
              <span>Total Price</span>
              <span>₹{total.toFixed(2)}</span>
            </div>
          </div>
          <button className="reserve-btn" onClick={handleReserve}>RESERVE</button>
        </div>
      </div>
    </div>
  );
}

export default VillaBooking;